import { Box, Tooltip, MantineStyleProp, Textarea } from '@mantine/core';
import {
  useRef,
  useState,
  useEffect,
  FC,
  MouseEvent,
  ChangeEvent,
} from 'react';
import { createPortal } from 'react-dom';
import classes from './index.module.scss';

const inputStyles = {
  padding: '0.635rem 0.875rem',
  borderRadius: 'inherit',
  fontSize: 'var(--mantine-font-size-sm)',
  lineHeight: 'var(--mantine-line-height)',
  color: 'transparent',
  caretColor: 'black',
  whiteSpace: 'pre-wrap',
  width: '100%',
  border: 'none',
  background: 'none',
};

export interface Variable {
  key: string;
  value: string;
}

interface EditableTextFieldProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  variables?: Variable[];
  required?: boolean;
  style?: MantineStyleProp;
}

const EditableTextField: FC<EditableTextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  variables,
  style,
  required = false,
}) => {
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const editorMirrorRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState('');
  const isUpdatingRef = useRef(false);
  const [tooltipData, setTooltipData] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const highlightVariables = (text: string, ignoreCursor = false) => {
    if (!editorMirrorRef.current || isUpdatingRef.current) return;

    if (!ignoreCursor) return;

    let html = text;
    let hasChanges = false;

    variables?.forEach((variable) => {
      const escapedKey = variable.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedKey, 'g');

      if (regex.test(html)) {
        hasChanges = true;
        html = html.replace(
          regex,
          `<span class="${classes.variable}">${variable.key}<span data-tooltip="${variable.value}" class="${classes.variableKey}">${variable.key}</span></span>`
        );
      }
    });

    if (hasChanges) {
      isUpdatingRef.current = true;

      // Update content
      if (editorMirrorRef.current) {
        editorMirrorRef.current.innerHTML = html;
      }

      isUpdatingRef.current = false;
    }
    setMessage(html);
  };

  const handleInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isUpdatingRef.current && editorRef.current) {
      const text = editorRef.current.value || '';
      highlightVariables(text);
      onChange(text);
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains(classes.variableKey)) {
      const tooltip = target.getAttribute('data-tooltip');
      if (tooltip) {
        const rect = target.getBoundingClientRect();
        setTooltipData({
          text: tooltip,
          x: rect.left + rect.width / 2,
          y: rect.bottom - rect.height / 2 - 10,
        });
      }
    } else {
      setTooltipData(null);
    }
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (value) {
      onChange(value);
      highlightVariables(value, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    highlightVariables(value || '', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  useEffect(() => {
    if (!editorRef.current) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.target.clientHeight;
        if (editorMirrorRef.current) {
          console.log(height);
          editorMirrorRef.current.style.height = `${height}px`;
        }
      }
    });

    resizeObserverRef.current.observe(editorRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  return (
    <Box pos="relative" onMouseLeave={handleMouseLeave}>
      {label && (
        <div className={classes.label}>
          {label} {required && '*'}
        </div>
      )}
      <Box pos="relative" className={classes.editorContainer}>
        <Textarea
          autosize
          ref={editorRef}
          className={classes.hiddenEditor}
          placeholder={placeholder}
          onChange={handleInput}
          value={value}
          styles={{
            input: inputStyles,
          }}
        />
        <Box
          ref={editorMirrorRef}
          style={{
            ...inputStyles,
            ...style,
            color: 'inherit',
          }}
          dangerouslySetInnerHTML={{ __html: message }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </Box>

      {tooltipData &&
        createPortal(
          <Tooltip
            opened
            label={tooltipData.text}
            position="top"
            multiline
            color="white"
            styles={{
              tooltip: {
                color: 'var(--text-color)',
                border: '1px solid var(--mantine-color-gray-4)',
              },
            }}
          >
            <div
              style={{
                position: 'fixed',
                left: tooltipData.x,
                top: tooltipData.y,
                width: 1,
                height: 1,
                pointerEvents: 'none',
              }}
            />
          </Tooltip>,
          document.body
        )}
    </Box>
  );
};

export default EditableTextField;
